import { RefObject, useCallback, MouseEvent as ReactMouseEvent } from 'react'

/**
 * Change state when vertical thumb is clicked down
 */
export const useOnVerticalThumbMouseDown = (
    contentRef: RefObject<HTMLDivElement>,
    setScrollStartPosition: (value: number) => void,
    setInitialScrollTop: (value: number) => void,
    setIsThumbDragging: (value: boolean) => void
) => {
    return useCallback(
        (event: ReactMouseEvent) => {
            event.preventDefault()
            event.stopPropagation()
            setScrollStartPosition(event.clientY)

            if (contentRef.current) {
                setInitialScrollTop(contentRef.current.scrollTop)
            }
            setIsThumbDragging(true)
        },
        [
            contentRef.current,
            setScrollStartPosition,
            setInitialScrollTop,
            setIsThumbDragging,
        ]
    )
}

/**
 * Handle vertical thumb click event up
 * @param isThumbDragging
 * @param setIsThumbDragging
 */
export const useOnVerticalThumbMouseUp = (
    isThumbDragging: boolean,
    setIsThumbDragging: (value: boolean) => void
) => {
    return useCallback(
        (event: ReactMouseEvent) => {
            event.preventDefault()
            event.stopPropagation()

            if (isThumbDragging) {
                setIsThumbDragging(false)
            }
        },
        [isThumbDragging, setIsThumbDragging]
    )
}

/**
 * Vertical scroll track click event handler
 * @param scrollTrackRef
 * @param contentRef
 * @param thumbHeight
 */
export const useOnVerticalTrackClick = (
    scrollTrackRef: RefObject<HTMLDivElement>,
    contentRef: RefObject<HTMLDivElement>,
    thumbHeight: number
) => {
    return useCallback(
        (event: ReactMouseEvent) => {
            event.preventDefault()
            event.stopPropagation()

            const { current: track } = scrollTrackRef
            const { current: content } = contentRef

            if (track && content) {
                const { clientY } = event
                const target = event.target as HTMLDivElement
                const rect = target.getBoundingClientRect()
                const trackTop = rect.top
                const thumbOffset = -(thumbHeight / 2)
                const clickRatio =
                    (clientY - trackTop + thumbOffset) / track.clientHeight
                const scrollAmount = Math.floor(
                    clickRatio * content.scrollHeight
                )
                content.scrollTo({
                    top: scrollAmount,
                    behavior: 'smooth',
                })
            }
        },
        [scrollTrackRef.current, contentRef.current, thumbHeight]
    )
}

/**
 * Vertical scroll thumb drag event handler
 * @param contentRef
 * @param isThumbDragging
 * @param scrollStartPosition
 * @param initialContentScrollTop
 * @param thumbHeight
 */
export const useOnVerticalThumbMouseMove = (
    contentRef: RefObject<HTMLDivElement>,
    isThumbDragging: boolean,
    scrollStartPosition: number,
    initialContentScrollTop: number,
    thumbHeight: number
) => {
    return useCallback(
        (event: ReactMouseEvent) => {
            if (contentRef.current) {
                event.preventDefault()
                event.stopPropagation()
                if (isThumbDragging) {
                    const {
                        scrollHeight: contentScrollHeight,
                        clientHeight: contentClientHeight,
                    } = contentRef.current

                    const deltaY =
                        (event.clientY - scrollStartPosition) *
                        (contentClientHeight / thumbHeight)

                    contentRef.current.scrollTop = Math.min(
                        initialContentScrollTop + deltaY,
                        contentScrollHeight - contentClientHeight
                    )
                }
            }
        },
        [
            contentRef.current,
            isThumbDragging,
            scrollStartPosition,
            initialContentScrollTop,
            thumbHeight,
        ]
    )
}

/**
 * Vertical mouse scroll event handler
 */
export const useOnVerticalThumbMouseWheelScroll = (
    contentRef: RefObject<HTMLDivElement>,
    scrollTrackRef: RefObject<HTMLDivElement>,
    scrollThumbRef: RefObject<HTMLDivElement>,
    thumbHeight: number
) => {
    return useCallback(() => {
        if (
            !contentRef.current ||
            !scrollTrackRef.current ||
            !scrollThumbRef.current
        ) {
            return
        }

        const { scrollTop: contentTop, scrollHeight: contentHeight } =
            contentRef.current
        const { clientHeight: trackHeight } = scrollTrackRef.current

        let newTop = (contentTop / contentHeight) * trackHeight
        newTop = Math.min(newTop, trackHeight - thumbHeight)

        const vThumb = scrollThumbRef.current
        requestAnimationFrame(() => {
            vThumb.style.top = `${newTop}px`
        })
    }, [contentRef.current, scrollTrackRef.current, scrollThumbRef.current])
}
